<template>
  <div>
    <el-button type="success" @click="openJobDialog">自動入力</el-button>
    <vue-final-modal
      v-model="showJobDialog"
      name="job-auto-dialog"
      :classes="['job-auto-dialog__container']"
      :content-class="['job-auto-dialog__content', 'v--modal-box', 'v--modal']"
      :click-to-close="true"
      :lock-scroll="true"
      :z-index-base="10000000"
      attach="body"
    >
      <div class="v--modal-close-top">
        <el-button
          @click="closeDialog"
          icon="el-icon-close"
          type="text"
        ></el-button>
      </div>
      <div class="title">職歴自動入力</div>
      <p>会社名と在籍期間を元に、入社・退社表記を自動で作成します。</p>
      <p>&nbsp;</p>
      <div class="edit-form">
        <div class="edit-form-item">
          <div class="item-name">会社名</div>
          <div class="item-body">
            <el-input
              class="input-md"
              placeholder="会社名を入力"
              title="会社名"
              v-model="company.name"
              @keyup.native.enter="formLib.nextInput($refs.startYear)"
              ref="companyName"
            ></el-input>
          </div>
        </div>
        <div class="edit-form-item">
          <div class="item-name">在籍期間</div>
          <div class="item-body job-auto-input-period">
            <div class="period-start">
              <el-input
                class="input-xxs"
                placeholder="2000"
                title="年"
                v-model="company.startYear"
                type="number"
                :min="calcMinYear"
                :max="calcMaxYear"
                :maxlength="4"
                @keyup.native.enter="formLib.nextInput($refs.startMonth)"
                ref="startYear"
              ></el-input
              ><span>年</span>
              <el-input
                class="input-xxs input-2-digit"
                placeholder="1"
                title="月"
                v-model="company.startMonth"
                type="number"
                min="1"
                max="12"
                @keyup.native.enter="formLib.nextInput($refs.endYear)"
                ref="startMonth"
              >
              </el-input
              ><span>月から</span>
            </div>
            <div class="period-end">
              <el-input
                class="input-xxs"
                placeholder="2018"
                title="年"
                v-model="company.endYear"
                :min="calcMinYear"
                :max="calcMaxYear"
                type="number"
                ref="endYear"
                @keyup.native.enter="formLib.nextInput($refs.endMonth)"
                :disabled="isContinuing"
              ></el-input
              ><span>年</span>
              <el-input
                class="input-xxs input-2-digit"
                placeholder="12"
                title="月"
                v-model="company.endMonth"
                type="number"
                min="1"
                max="12"
                ref="endMonth"
                :disabled="isContinuing"
              >
              </el-input
              ><span>月まで</span>
              <div class="tenure">
                <el-checkbox v-model="isContinuing">現在も勤務中</el-checkbox>
              </div>
            </div>
          </div>
        </div>
        <div class="edit-form-item" v-if="!isContinuing">
          <div class="item-name">退職理由</div>
          <div class="item-body">
            <div class="radio-horizontal">
              <el-radio
                v-for="item in retirementReasonOptions"
                :key="item.value"
                v-model="retirementReason"
                :label="item.value"
                >{{ item.label }}</el-radio
              >
            </div>
          </div>
        </div>
      </div>
      <div class="text-right">
        <el-button type="primary" @click="setJobHistories">追加する</el-button>
        <el-button @click="closeDialog">閉じる</el-button>
        <div v-show="errorMessage">{{ errorMessage }}</div>
      </div>
      <div class="text-right">
        <br />
        <br />
      </div>
    </vue-final-modal>
  </div>
</template>

<style lang="scss">
@import '@/assets/scss/format-edit/vfm-attach-body-fixed';
@include vfm-root-fixed-when-has('.job-auto-dialog__container');

.job-auto-dialog__container {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 20px 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.job-auto-dialog__content.v--modal-box.v--modal {
  position: relative;
  box-sizing: border-box;
  flex: 0 1 auto;
  align-self: center;
  width: 600px;
  max-width: 100%;
  height: auto;
  max-height: 90vh;
  overflow-y: auto;
  margin-block: auto;
  padding: 20px 20px 50px;
  left: 0 !important;
}
</style>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import retirementReasonOptions from '@/utils/format-edit/retirementReasonOptions.json'
import {
  buildJobHistories,
  createEmptyCompany,
  isCompanyEmpty,
  type JobHistoryListItem
} from '@/utils/format-edit/jobHistoryAuto'

const props = defineProps<{
  birthYear?: number | string
  birthMonth?: number | string
  birthDay?: number | string
}>()

const emit = defineEmits<{
  (e: 'job-histories-created', list: JobHistoryListItem[]): void
}>()

const { formLib } = useHostServices()

const showJobDialog = ref(false)
const isContinuing = ref(false)
const retirementReason = ref('')
const errorMessage = ref('')
const company = reactive(createEmptyCompany())

const calcMinYear = computed(() =>
  props.birthYear ? Number(props.birthYear) + 15 : 1900
)
const calcMaxYear = computed(() => new Date().getFullYear() + 3)

function resetCompany() {
  Object.assign(company, createEmptyCompany())
  isContinuing.value = false
}

function openJobDialog() {
  resetCompany()
  showJobDialog.value = true
}

function closeDialog() {
  showJobDialog.value = false
}

function setJobHistories() {
  errorMessage.value = ''
  if (isCompanyEmpty(company)) {
    return
  }
  const jobHistories = buildJobHistories({
    company,
    isContinuing: isContinuing.value,
    retirementReason: retirementReason.value
  })
  emit('job-histories-created', jobHistories)
  closeDialog()
}
</script>
